/**
 * Day-scoped reflection scalars (Phase 6, ADR-0002 addendum 2026-07-07):
 * sleep hours (R8.5), favorite thing about today (R8.3), gratitude (R8.6),
 * and close-the-day (R8.9). Grouped in one module because all four are
 * simple last-write-wins (or per-index last-write-wins, for gratitude)
 * derivations over one-event-per-write types, feeding `DailyPageView`'s
 * day-scoped fields exactly like `checkin/daily-fields.ts` already does for
 * mood/energy/note -- this is the same pattern applied to four more fields
 * rather than four new single-field modules.
 */

import type {
  ClockPort,
  CompassEvent,
  DayClosedEvent,
  EventId,
  FavoriteMomentSetEvent,
  GratitudeSetEvent,
  IdPort,
  IsoDate,
  LifeAreaTag,
  SleepHoursSetEvent,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

const FAVORITE_MOMENT_REFLECTION_MAX_CHARS = 240;
const SLEEP_HOURS_MIN = 0;
const SLEEP_HOURS_MAX = 24;

// ---------------------------------------------------------------------------
// Sleep hours (R8.5) -- one scalar, last-write-wins, mirrors mood/energy.
// ---------------------------------------------------------------------------

export interface SleepHoursField {
  readonly hours: number | null;
  readonly eventId: EventId | null;
}

export function computeSleepHours(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): SleepHoursField {
  // excludingReverted MUST run over the full log before narrowing by type --
  // revertedEventIds needs to see the log_entry.reverted events themselves,
  // which eventsOfTypes(events, ['checkin.sleep_hours_set']) would already
  // have filtered out (mirrors computeDailyFields's own ordering exactly).
  const relevant = eventsOfTypes(excludingReverted(events), ['checkin.sleep_hours_set']).filter(
    (e) => e.occurredOn === date,
  );
  if (relevant.length === 0) return { hours: null, eventId: null };
  const last = relevant[relevant.length - 1]!;
  return { hours: last.hours, eventId: last.eventId };
}

/** `hours` validated 0-24 (decimals allowed) at command time (spec §4's explicit scoping). */
export function setSleepHours(
  date: IsoDate,
  hours: number,
  ports: Ports,
): CommandResult<SleepHoursSetEvent> {
  if (!Number.isFinite(hours) || hours < SLEEP_HOURS_MIN || hours > SLEEP_HOURS_MAX) {
    throw domainError(
      'invalid_input',
      `sleep hours must be between ${SLEEP_HOURS_MIN} and ${SLEEP_HOURS_MAX}, got ${hours}`,
    );
  }

  const event: SleepHoursSetEvent = {
    eventId: ports.id.nextEventId(),
    type: 'checkin.sleep_hours_set',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    hours,
  };

  return { events: [event] };
}

// ---------------------------------------------------------------------------
// Favorite thing about today (R8.3) -- one scalar (tags + reflection),
// last-write-wins (whole event -- re-submitting replaces both fields
// together, matching the event's own single-payload shape).
// ---------------------------------------------------------------------------

export interface FavoriteMomentField {
  readonly tags: readonly LifeAreaTag[];
  readonly reflection: string;
  readonly eventId: EventId;
}

export function computeFavoriteMoment(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): FavoriteMomentField | null {
  const relevant = eventsOfTypes(excludingReverted(events), ['checkin.favorite_moment_set']).filter(
    (e) => e.occurredOn === date,
  );
  if (relevant.length === 0) return null;
  const last = relevant[relevant.length - 1]!;
  return { tags: last.tags, reflection: last.reflection, eventId: last.eventId };
}

/**
 * `tags` validated 0-5 entries, no duplicates; `reflection` REJECTED beyond
 * the 240-char bound (never silently truncated -- AC-T9's explicit "never a
 * silent failure").
 */
export function setFavoriteMoment(
  date: IsoDate,
  input: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string },
  ports: Ports,
): CommandResult<FavoriteMomentSetEvent> {
  if (input.tags.length > 5) {
    throw domainError('invalid_input', `at most 5 LifeAreaTag selections, got ${input.tags.length}`);
  }
  if (new Set(input.tags).size !== input.tags.length) {
    throw domainError('invalid_input', 'LifeAreaTag selections must not contain duplicates');
  }
  if (input.reflection.length > FAVORITE_MOMENT_REFLECTION_MAX_CHARS) {
    throw domainError(
      'invalid_input',
      `reflection must be at most ${FAVORITE_MOMENT_REFLECTION_MAX_CHARS} characters, got ${input.reflection.length}`,
    );
  }

  const event: FavoriteMomentSetEvent = {
    eventId: ports.id.nextEventId(),
    type: 'checkin.favorite_moment_set',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    tags: input.tags,
    reflection: input.reflection,
  };

  return { events: [event] };
}

// ---------------------------------------------------------------------------
// Gratitude (R8.6) -- fixed 3-tuple, per-INDEX last-write-wins (see
// GratitudeSetEvent's own doc comment, events.ts).
// ---------------------------------------------------------------------------

export type GratitudeSlots = readonly [string, string, string];

const EMPTY_GRATITUDE: GratitudeSlots = ['', '', ''];

export function computeGratitude(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): GratitudeSlots {
  const relevant = eventsOfTypes(excludingReverted(events), ['checkin.gratitude_set']).filter(
    (e) => e.occurredOn === date,
  );
  if (relevant.length === 0) return EMPTY_GRATITUDE;

  // Per-index resolution: each slot independently takes the LAST surviving
  // write that actually supplied a value for it. Every write in this event
  // shape supplies all 3 positions (the command layer merges before
  // emitting, per the event's own doc comment), so "last write wins" per
  // index reduces to "the last event's value at that index" -- but walking
  // per-index rather than just taking the last event's whole tuple keeps this
  // correct even if a future writer ever emits a partial-looking tuple.
  const slots: [string, string, string] = ['', '', ''];
  for (const event of relevant) {
    slots[0] = event.slots[0];
    slots[1] = event.slots[1];
    slots[2] = event.slots[2];
  }
  return slots;
}

/**
 * One slot's write (0/1/2 = slots 1-3 in product language). The command
 * layer reads the OTHER two slots' currently-resolved values and re-emits a
 * full tuple internally (the event's own doc comment) -- callers of this
 * function never need to know or supply the other two.
 */
export function setGratitudeSlot(
  events: readonly CompassEvent[],
  date: IsoDate,
  slot: 0 | 1 | 2,
  text: string,
  ports: Ports,
): CommandResult<GratitudeSetEvent> {
  const current = [...computeGratitude(events, date)] as [string, string, string];
  current[slot] = text;

  const event: GratitudeSetEvent = {
    eventId: ports.id.nextEventId(),
    type: 'checkin.gratitude_set',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    slots: current,
  };

  return { events: [event] };
}

// ---------------------------------------------------------------------------
// Close the day (R8.9) -- date-keyed, idempotent, non-gating (D-CloseTheDay).
// ---------------------------------------------------------------------------

export interface DayClosedField {
  readonly closed: boolean;
  readonly eventId: EventId | null;
}

/**
 * True iff a surviving (non-reverted) `day.closed` event exists for `date`
 * -- the FIRST (canonical order) surviving one is the one whose eventId is
 * exposed (mirrors `checkin.completed`'s "duplicates collapse, first
 * earns XP" convention: the first is the canonical entry to revert).
 */
export function computeDayClosed(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): DayClosedField {
  const relevant = eventsOfTypes(excludingReverted(events), ['day.closed']).filter(
    (e) => e.occurredOn === date,
  );
  if (relevant.length === 0) return { closed: false, eventId: null };
  return { closed: true, eventId: relevant[0]!.eventId };
}

/**
 * Non-gating by construction: this function is a pure event constructor with
 * no precondition beyond the envelope itself -- no other command in this
 * codebase consults `day.closed` before allowing a write (AC-T22 is
 * satisfied structurally, not by a runtime check here). Idempotent XP
 * (AC-T21) is enforced downstream by the reward engine's award-collapse
 * rule (mirrors `checkin.completed`), not by rejecting a second call here --
 * re-closing an already-closed day is allowed and simply earns no further
 * XP.
 */
export function closeDay(date: IsoDate, ports: Ports): CommandResult<DayClosedEvent> {
  const event: DayClosedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'day.closed',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
  };

  return { events: [event] };
}
